import type { Effect } from 'effector'
import type { ApiAbortableEffect, ApiEffect } from '../../index.h'

import { attach, createEffect } from 'effector'

type ControllerId = string
type ControllersMap = Map<ControllerId, AbortController>

const AUTO_ABORT_PREVIOUS_DEFAULT = false

/*
 * This function returns createEffect with linked controllers map
 */
function getCreateAbortableEffect(controllers: ControllersMap) {
  /*
   * This function auto aborts previus effect call.
   * Also stores controller to Map for more advanced managment.
   */
  return function createAbortableEffect<T, Y>(args?: {
    name?: string
    autoAbort?: boolean
  }): [ApiEffect<T, Y>, ApiAbortableEffect<T, Y>] {
    const name = args?.name
    const autoAbort = args?.autoAbort ?? AUTO_ABORT_PREVIOUS_DEFAULT

    const originalEffectFX: ApiEffect<T, Y> = createEffect(name)

    let scopeController:
      | { id: ControllerId; controller: AbortController }
      | undefined

    const abortableEffectFx = attach({
      effect: originalEffectFX,
      mapParams: (params) => {
        const controllersMap = controllers

        if (autoAbort && scopeController) {
          scopeController.controller.abort()
          controllersMap.delete(scopeController.id)
        }

        const id = String(controllersMap.size)
        const controller = new AbortController()
        scopeController = { id, controller }
        controllersMap.set(id, controller)

        return {
          ...params,
          signal: controller.signal,
        }
      },
    })

    // original is used by tria api and adapter
    // abortable is for the in app use
    return [originalEffectFX, abortableEffectFx]
  }
}

function abortMap(map: Map<string, AbortController>): void {
  // Abort each controller in the map and then clear the map.
  for (const [, controller] of map.entries()) {
    controller.abort()
  }

  map.clear()
}

export function abortFeature(): {
  createAbortableEffect: typeof createAbortableEffect
  abort: Effect<void, void, Error>
} {
  // exact module controllers, which called it
  const controllers = new Map()

  // effect to abort all controllers of module
  const abort = createEffect(() => {
    abortMap(controllers)
  })

  const createAbortableEffect = getCreateAbortableEffect(controllers)

  return {
    createAbortableEffect,
    abort,
  }
}
